import type { FlexProps } from '@chakra-ui/react';
import {
  Button,
  Flex,
  Skeleton,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useDisclaimerDrawer } from '@ui/components/DisclaimerDrawer';
import { HorizontalScrolling } from '@ui/components/HorizontalScrolling';
import { HorizontalScrollingButton } from '@ui/components/HorizontalScrollingButton';
import { InfoWithLoading } from '@ui/components/Info';
import { InDesktop, InMobile } from '@ui/components/MobileOrDesktop';
import {
  NFTCard,
  nftMarketplaces,
  sellNftOnMinted,
  useNftClaimDrawerStore,
} from '@ui/components/NFTCard';
import WithWallet from '@ui/components/Wallet/WithWallet';
import { pathDict } from '@ui/config/paths';
import { useBalance } from '@ui/hooks/useBalance';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import useNftTokens from '@ui/hooks/useNftTokens';
import { useTranslations } from '@ui/i18n';
import NextLink from '@ui/overrides/next/link';
import { formatTokenAmount } from '@ui/utils/format';
import getBgImage from '@ui/utils/getBgImageProps';
import { CRONOS_CHAIN_ID, currentWallet } from '@veno-app/wallet';
import type { NextPage } from 'next';
import { useState } from 'react';

export const ClaimView: NextPage = () => {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const bgImageProps =
    globalCoinMode === 'atom'
      ? getBgImage('PATTERN_2_ATOM')
      : globalCoinMode === 'eth'
      ? getBgImage('PATTERN_UNSTAKE_ETH')
      : getBgImage('PATTERN_2_1');
  return (
    <>
      <InMobile>
        <WithWallet
          pt={9}
          px={4}
          actionHint={t('see your NFT receipts')}
          minH="572px"
          {...bgImageProps}
          bgPos={globalCoinMode === 'eth' ? undefined : 'left top 20px'}
        >
          <RedeemableNftInfo />
          <Spacer mt="50px" />
          <PendingNftInfo />
        </WithWallet>
      </InMobile>
      <InDesktop>
        <Tabs>
          <TabList>
            <Tab>{t('Redeemable')}</Tab>
            <Tab>{t('Pending')}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <RedeemableNftInfo />
            </TabPanel>
            <TabPanel>
              <PendingNftInfo />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </InDesktop>
    </>
  );
};

const RedeemableNftInfo = () => {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[globalCoinMode];
  const {
    isLoading,
    redeemableNftTokens,
    redeemableTokenAmount,
    redeemableUsdAmount,
  } = useNftTokens();
  const openNftClaimAllDrawer = useNftClaimDrawerStore((s) => s.onOpen);
  const [nftContainer, setNftContainer] = useState<HTMLDivElement | null>(null);
  const chainId = currentWallet.useChainId();
  const openDisclaimerDrawer = useDisclaimerDrawer((s) => s.onOpen);

  return (
    <>
      <InfoWithLoading
        isLoading={isLoading}
        title={t('Redeemable {coin}', {
          coin: 'NFTs',
        })}
        icon={(props) => <coinModeInfo.Icon {...props} />}
        value={
          formatTokenAmount(
            redeemableTokenAmount,
            undefined,
            coinModeInfo.decimals,
          ) ?? undefined
        }
        convertedValue={`${formatTokenAmount(redeemableUsdAmount) || ''} USD`}
      />
      {redeemableNftTokens.length ? (
        <>
          <Button
            size="sm"
            mt="4px"
            onClick={() => {
              openNftClaimAllDrawer({
                coinMode: globalCoinMode,
                nfts: redeemableNftTokens,
              });
            }}
          >
            {t('Claim all')}
          </Button>
          <InDesktop>
            <HorizontalScrollingButton
              scrollContainer={nftContainer}
              justify="end"
              mt="16px"
            />
          </InDesktop>
          <HorizontalScrolling
            ref={setNftContainer}
            mx={{
              base: '-16px',
              desktop: '-24px',
            }}
            mt="10px"
          >
            {redeemableNftTokens.map((token, i) => {
              return <NFTCard key={i} coinMode={globalCoinMode} nft={token} />;
            })}
          </HorizontalScrolling>
        </>
      ) : (
        <Empty tip={t('You have no redeemable receipts')} isLoading={isLoading}>
          <Button
            onClick={() => {
              sellNftOnMinted(
                undefined,
                globalCoinMode,
                openDisclaimerDrawer,
                chainId ?? CRONOS_CHAIN_ID,
              );
            }}
          >
            {t('Buy {coin} on {platform}', {
              coin: 'NFT',
              platform: nftMarketplaces[chainId ?? CRONOS_CHAIN_ID],
            })}
          </Button>
        </Empty>
      )}
    </>
  );
};

const PendingNftInfo = () => {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[globalCoinMode];
  const { isLoading, pendingNftTokens, pendingUsdAmount, pendingTokenAmount } =
    useNftTokens();
  const balance = useBalance();
  const [nftContainer, setNftContainer] = useState<HTMLDivElement | null>(null);

  return (
    <>
      <InfoWithLoading
        isLoading={isLoading}
        title={t('Pending {coin}', {
          coin: 'NFTs',
        })}
        icon={(props) => <coinModeInfo.Icon {...props} />}
        value={
          formatTokenAmount(
            pendingTokenAmount,
            undefined,
            coinModeInfo.decimals,
          ) ?? undefined
        }
        convertedValue={`${formatTokenAmount(pendingUsdAmount) || ''} USD`}
        convertedValueLoading={isLoading}
      />
      {pendingNftTokens.length ? (
        <>
          <InDesktop>
            <HorizontalScrollingButton
              scrollContainer={nftContainer}
              justify="end"
              mt="16px"
            />
          </InDesktop>
          <HorizontalScrolling
            ref={setNftContainer}
            mx={{
              base: '-16px',
              desktop: '-24px',
            }}
            mt="16px"
          >
            {pendingNftTokens.map((token, i) => {
              return (
                <NFTCard
                  key={i}
                  coinMode={globalCoinMode}
                  nft={token}
                  showUnlockTime
                />
              );
            })}
          </HorizontalScrolling>
        </>
      ) : balance ? (
        <Empty tip={t('You have no pending receipts')} isLoading={isLoading}>
          <InMobile>
            <NextLink href={pathDict[globalCoinMode].unstake} passHref>
              <Button as="a">
                {t('Unstake {coin}', {
                  coin: coinModeInfo.name,
                })}
              </Button>
            </NextLink>
          </InMobile>
        </Empty>
      ) : (
        <Empty
          tip={t('You have no {coin} in your wallet', {
            coin: coinModeInfo.liquidName,
          })}
        >
          <NextLink href={pathDict[globalCoinMode].stake} passHref>
            <Button as="a">
              {t('Stake {coin}', {
                coin: coinModeInfo.name,
              })}
            </Button>
          </NextLink>
        </Empty>
      )}
    </>
  );
};

const Empty = ({
  tip,
  children,
  isLoading,
  ...props
}: { tip: string; children: JSX.Element; isLoading?: boolean } & FlexProps) => {
  return (
    <Flex
      mt="28px"
      direction="column"
      gap="24px"
      alignItems="center"
      {...props}
    >
      {!isLoading && <Text color="text-light">{tip}</Text>}
      {isLoading && <Skeleton width="206px" height="24px" />}
      {children}
    </Flex>
  );
};
